/**
 * React hooks for workspace data management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { workspaceService } from '../services';
import type { Workspace, WorkspaceCreateRequest, WorkspaceUpdateRequest, PaginationOptions, WorkspaceStatistics, WorkspaceFilterOptions } from '../types';

const WORKSPACE_QUERY_KEYS = {
  all: ['workspaces'] as const,
  lists: () => [...WORKSPACE_QUERY_KEYS.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...WORKSPACE_QUERY_KEYS.lists(), { filters }] as const,
  details: () => [...WORKSPACE_QUERY_KEYS.all, 'detail'] as const,
  detail: (tenantId: string) => [...WORKSPACE_QUERY_KEYS.details(), tenantId] as const,
};

/**
 * Hook to fetch workspaces with pagination and filtering
 */
export function useWorkspaces(options?: PaginationOptions, filters?: WorkspaceFilterOptions) {
  const filtersKey = JSON.stringify(filters || {});
  const pageKey = options?.nextToken || 'page-1';
  
  return useQuery({
    queryKey: ['workspaces', 'page', filtersKey, pageKey],
    queryFn: () => workspaceService.getWorkspaces(options, filters),
    staleTime: 30 * 1000, // 30 seconds cache for better UX
    gcTime: 5 * 60 * 1000, // 5 minutes garbage collection
    refetchOnMount: false,
    refetchOnReconnect: false,
    structuralSharing: false, // Prevent data merging
  });
}

/**
 * Hook to fetch a specific workspace
 */
export function useWorkspace(tenantId: string, enabled = true) {
  return useQuery({
    queryKey: WORKSPACE_QUERY_KEYS.detail(tenantId),
    queryFn: () => workspaceService.getWorkspace(tenantId),
    enabled: enabled && !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to create a new workspace
 */
export function useCreateWorkspace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: WorkspaceCreateRequest) => workspaceService.createWorkspace(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WORKSPACE_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: [...WORKSPACE_QUERY_KEYS.all, 'statistics'] });
    },
  });
}

/**
 * Hook to update an existing workspace
 */
export function useUpdateWorkspace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenantId, data }: { tenantId: string; data: WorkspaceUpdateRequest }) =>
      workspaceService.updateWorkspace(tenantId, data),
    onSuccess: (updatedWorkspace: Workspace) => {
      queryClient.setQueryData(
        WORKSPACE_QUERY_KEYS.detail(updatedWorkspace.tenantId),
        updatedWorkspace
      );
      queryClient.invalidateQueries({ queryKey: WORKSPACE_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: [...WORKSPACE_QUERY_KEYS.all, 'statistics'] });
    },
  });
}

/**
 * Hook to delete a workspace
 */
export function useDeleteWorkspace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tenantId: string) => workspaceService.deleteWorkspace(tenantId),
    onSuccess: (_: void, deletedTenantId: string) => {
      queryClient.removeQueries({ queryKey: WORKSPACE_QUERY_KEYS.detail(deletedTenantId) });
      queryClient.invalidateQueries({ queryKey: WORKSPACE_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: [...WORKSPACE_QUERY_KEYS.all, 'statistics'] });
    },
  });
}

/**
 * Hook to fetch workspace statistics with optional filtering
 */
export function useWorkspaceStatistics(filters?: WorkspaceFilterOptions) {
  return useQuery({
    queryKey: [...WORKSPACE_QUERY_KEYS.all, 'statistics', filters],
    queryFn: () => workspaceService.getWorkspaceStatistics(filters),
    staleTime: 1 * 60 * 1000, // 1 minute - cache statistics for shorter time to see updates faster
  });
}
